<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { computed } from 'vue';

import HorizontalProductCard from '@/components/base/layout/HorizontalProductCard.vue';
import Slider from '@/components/base/Slider.vue';
import TagBadge from '@/components/base/TagBadge.vue';
import Caption from '@/components/base/typography/Caption.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import money from '@/filters/money';
import { BaseChoice } from '@/lib/personalization/dynamicYield';
import { ProductClickedPageSection } from '@/utils/analytics/productClickedEvent';
import { ProductListViewedEventData } from '@/utils/analytics/productListViewedEvent';
import { displayPrice, ProductCardData } from '@/utils/productCard';

withDefaults(
  defineProps<{
    analyticsList?: string;
    decisionId?: BaseChoice['decisionId'];
    link?: string;
    pageSection?: ProductClickedPageSection | ProductListViewedEventData['pageSection'];
    recommendations: ProductCardData[];
    title?: string;
    trackingEventLocation?: string;
  }>(),
  {
    title: 'You May Also like',
  },
);

const breakpoints = useBreakpoints(breakpointsTailwind);
const breakpointBelowSm = breakpoints.smaller('sm');
const breakpointBelowMd = breakpoints.smaller('md');
const scrollWidth = computed(() => {
  if (breakpointBelowSm.value) {
    return 344;
  }
  if (breakpointBelowMd.value) {
    return 306;
  }
  return undefined;
});

const { flags } = useFeatureFlags();
</script>

<template>
  <div v-if="recommendations.length" class="flex items-end" data-test="slim-recommendations">
    <Slider justifyContent="start" :link :list="recommendations" :scrollWidth :title>
      <HorizontalProductCard
        v-for="(recommendation, index) in recommendations"
        class="w-[344px] sm:w-[306px] md:w-[368px] lg:w-[330px] px-4 border-r border-solid border-neutral-200 last:border-0 snap-start snap-always md:snap-normal"
        :analyticsList
        :key="recommendation.sku"
        :decisionId
        imageSize="medium"
        impressionTrackable
        :index
        :pageSection
        :product="recommendation"
        textSize="small"
        :trackingEventLocation
      >
        <template v-slot:badge>
          <TagBadge
            class="absolute top-0 left-4"
            alignment="left"
            :tags="recommendation.totalSavings ? ['badge-onsale'] : recommendation.keywords ?? []"
          />
        </template>
        <template v-slot:price>
          <span v-if="recommendation.totalSavings" class="sr-only">Price reduced from </span>
          <SmallBodyText
            :class="recommendation.totalSavings?.onSale ? 'text-nuts-red-800' : 'text-neutral-500'"
          >
            {{
              recommendation.hidePrice
                ? 'Build your own'
                : displayPrice(recommendation, flags.testVariantNameDisplay)
            }}
          </SmallBodyText>
          <span v-if="recommendation.totalSavings" class="sr-only"> to </span>
          <Caption v-if="recommendation.totalSavings" class="ml-1.5 line-through text-neutral-500">
            {{ money(recommendation.totalSavings.comparisonPrice) }}
          </Caption>
        </template>
      </HorizontalProductCard>
    </Slider>
  </div>
</template>
